import React from 'react';
import { Box, Button, Checkbox, Typography } from '@mui/material';
import PanelBlock, { PanelEyebrow } from './PanelBlock';
import { useLabBasket } from '../../contexts/LabBasketContext';
import { LABOKLIN_RED } from '../../theme/laboklinTheme';
import type { StructuredTest } from '../../types/laboklin';

/**
 * The tests the assistant offered, as an order the veterinarian can assemble.
 *
 * Every line here came back from suggest_lab_tests during the workup, so the name,
 * the service number and the price are the laboratory's own and not a recollection
 * of them. Each carries the question it settles, because a suggestion with no
 * question behind it reads as an advertisement.
 *
 * Nothing on this panel places an order. It fills the same in-memory basket the chat
 * drawer fills, and hands over to MyLab's submission form to be confirmed.
 */

interface NextTestsPanelProps {
  tests: StructuredTest[];
  onConfirm: () => void;
}

export const NextTestsPanel: React.FC<NextTestsPanelProps> = ({ tests, onConfirm }) => {
  const basket = useLabBasket();

  if (tests.length === 0) return null;

  const selected = tests.filter((t) => basket?.has(t.serviceNo));
  // A total is only honest when every selected line carries a price; one unpriced
  // service would make the sum quietly wrong.
  const allPriced = selected.every((t) => typeof t.price === 'number');
  const total = allPriced ? selected.reduce((sum, t) => sum + (t.price ?? 0), 0) : null;

  return (
    <PanelBlock>
      <PanelEyebrow label="Suggested additional tests" state="Live Laboklin catalogue" />

      {tests.map((test) => {
        const inBasket = basket?.has(test.serviceNo) ?? false;

        return (
          <Box
            key={test.serviceNo}
            sx={{
              display: 'grid',
              gridTemplateColumns: '32px 1fr auto',
              gap: 1,
              alignItems: 'start',
              py: 1.25,
              borderTop: '1px solid #eee',
              '&:first-of-type': { borderTop: 0 },
            }}
          >
            <Checkbox
              size="small"
              checked={inBasket}
              disabled={!basket}
              onChange={() => {
                if (!basket) return;
                if (inBasket) basket.remove(test.serviceNo);
                else
                  basket.add({
                    serviceNo: test.serviceNo,
                    name: test.name,
                    price: test.price,
                    ...(test.settles ? { reason: test.settles } : {}),
                  });
              }}
              sx={{ p: 0, mt: 0.25, color: LABOKLIN_RED, '&.Mui-checked': { color: LABOKLIN_RED } }}
              inputProps={{ 'aria-label': `Add ${test.name} to order` }}
            />
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{test.name}</Typography>
              {test.settles && (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{test.settles}</Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {test.price !== null ? `€ ${test.price.toFixed(2)}` : ''}
            </Typography>
          </Box>
        );
      })}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 1.5,
          pt: 1.5,
          borderTop: '2px solid rgba(0,0,0,0.125)',
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {selected.length} selected
          {total !== null && (
            <Box component="span" sx={{ color: 'text.primary', fontSize: 15, fontWeight: 600, ml: 0.75 }}>
              € {total.toFixed(2)}
            </Box>
          )}
        </Typography>
        <Button
          size="small"
          variant="contained"
          disabled={selected.length === 0}
          onClick={onConfirm}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: LABOKLIN_RED,
            '&:hover': { bgcolor: LABOKLIN_RED, filter: 'brightness(0.9)' },
          }}
        >
          Continue to order
        </Button>
      </Box>

      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 1 }}>
        Nothing is ordered here. The submission form is where an order is confirmed.
      </Typography>
    </PanelBlock>
  );
};

export default NextTestsPanel;
